import React, { useCallback, useMemo } from 'react';
import styled, { css } from 'styled-components';
import { ClockConfigType } from '../config/letters/config.types';
import { SkinType } from '../config/skins/skins.types';
import { BasicComponentsWrapper } from '../styles/BasicComponentsWrapper';
import { Button } from 'primereact/button';
import { Dropdown, DropdownChangeEvent } from 'primereact/dropdown';
import { CONFIG_DISPLAY_NAMES, CONFIG_MAP } from '../config/letters/config.map';
import { SKINS } from '../config/skins/skins.map';
import { Checkbox } from 'primereact/checkbox';
import { FloatLabel } from 'primereact/floatlabel';

interface Props {
  setConfig: (config: ClockConfigType) => void;
  setSkin: (skin: SkinType) => void;
  activeSkin: SkinType;
  activeConfig: ClockConfigType;
  isVisible: boolean;
  size: number;
  onHide: () => void;
  isFullscreen: boolean;
  setFullscreen: (v: boolean) => void;
}

// Extended OptionItem interface with key and displayName
interface OptionItem {
  key: string;
  displayName: string;
}

const Wrapper = styled.div<{ $size: number; $isFullscreen: boolean; $isVisible: boolean }>`
  background-color: #fff;
  ${props =>
    !props.$isVisible &&
    css`
      opacity: 0;
      scale: 0;
      z-index: -1;
    `};
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  ${BasicComponentsWrapper};
`;

const FieldGroupWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;

  .p-dropdown {
    width: 100%;
  }

  .p-float-label {
    margin-top: 20px;
  }
`;

const HeadlineWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  font-size: 30px;
`;

const CheckBoxWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-start;
  cursor: pointer;
  margin-top: 15px;

  label {
    margin-left: 10px;
    cursor: pointer;
  }
`;

const Footer = styled.div`
  display: flex;
  justify-content: center;
  font-size: 12px;
  margin-top: 15px;
  gap: 10px;
  color: #707070;

  a {
    color: #707070;
    text-decoration: none;
  }
`;

const BtnWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
`;

const Logo = styled.div`
  display: flex;
  width: 100%;
  justify-content: center;
  font-size: 12px;
  margin-top: 15px;
  gap: 10px;
  color: #707070;
  a {
    color: #707070;
    text-decoration: none;
  }
`;

const Settings: React.FC<Props> = React.memo(
  ({
    setConfig,
    setSkin,
    activeConfig,
    activeSkin,
    isVisible,
    size,
    onHide,
    isFullscreen,
    setFullscreen,
  }) => {
    const configOptions = useMemo(
      () =>
        Object.keys(CONFIG_MAP).map(
          (key): OptionItem => ({
            key,
            displayName: CONFIG_DISPLAY_NAMES[key as ClockConfigType] || key,
          })
        ),
      []
    );

    const skinOptions = useMemo(
      () =>
        Object.entries(SKINS).map(
          ([key, value]): OptionItem => ({
            key,
            displayName: value.displayName,
          })
        ),
      []
    );

    const handleConfigChange = useCallback(
      (e: DropdownChangeEvent) => {
        setConfig(e.value.key as ClockConfigType);
      },
      [setConfig]
    );

    const handleSkinChange = useCallback(
      (e: DropdownChangeEvent) => {
        setSkin(e.value.key as SkinType);
      },
      [setSkin]
    );

    const handleFullscreenChange = useCallback(
      (e: { checked?: boolean }) => {
        setFullscreen(e.checked ?? false);
      },
      [setFullscreen]
    );

    const configValue = useMemo(
      () => ({
        key: activeConfig,
        displayName: CONFIG_DISPLAY_NAMES[activeConfig] || activeConfig,
      }),
      [activeConfig]
    );

    const skinValue = useMemo(
      () => ({
        key: activeSkin,
        displayName: SKINS[activeSkin].displayName,
      }),
      [activeSkin]
    );

    return (
      <Wrapper $size={size} $isVisible={isVisible} $isFullscreen={isFullscreen}>
        <HeadlineWrapper>Settings</HeadlineWrapper>
        <FieldGroupWrapper>
          <FloatLabel>
            <Dropdown
              value={configValue}
              onChange={handleConfigChange}
              options={configOptions}
              optionLabel="displayName"
              dataKey="key"
              placeholder="Choose template"
              inputId="temlate-dd"
            />
            <label htmlFor="temlate-dd">Choose template</label>
          </FloatLabel>
          <FloatLabel>
            <Dropdown
              value={skinValue}
              onChange={handleSkinChange}
              options={skinOptions}
              optionLabel="displayName"
              dataKey="key"
              placeholder="Choose style"
              inputId="skin-dd"
            />
            <label htmlFor="skin-dd">Choose style</label>
          </FloatLabel>
          <CheckBoxWrapper>
            <Checkbox
              inputId="fullscreen-cb"
              name="fullscreen"
              onChange={handleFullscreenChange}
              checked={isFullscreen}
            />
            <label htmlFor="fullscreen-cb">Fullscreen mode</label>
          </CheckBoxWrapper>
        </FieldGroupWrapper>
        <BtnWrapper>
          <Button label="Apply" icon="pi pi-check" onClick={onHide} />
        </BtnWrapper>
        <div>
          <Footer>
            <span>
              <a href="https://bibertec.de/biberclock" target="_blank" rel="noreferrer">
                About this App
              </a>
            </span>
            <span>•</span>
            <span className="text-sm font-(family-name:--font-primary) text-(--gray)">
              <a href="https://bibertec.de/imprint" target="_blank" rel="noreferrer">
                Imprint
              </a>
            </span>
            <span>•</span>
            <span>
              <a href="https://bibertec.de/privacy" target="_blank" rel="noreferrer">
                Privacy
              </a>
            </span>
          </Footer>
          <Logo>
            ©
            <a href="https://bibertec.de" target="_blank" rel="noreferrer">
              bibertec
            </a>
          </Logo>
        </div>
      </Wrapper>
    );
  }
);

export default Settings;
